/* Copyright (c) 2020-present CloudIO Inc */

import { LogFilter } from '@cloudio-saas/datasource-types';
import { useEvent, useStateXAtom } from '@cloudio-saas/statex';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { Platform } from 'componentregistry';
import { getUrl } from 'config/config';
import { formatDistanceToNow, parse } from 'date-fns';
import FileSaver from 'file-saver';
import Icon from 'icons/Icon';
import stringify from 'json-stable-stringify';
import {
  LogFile,
  serverLogsActiveEntryAtom,
  serverLogsEntriesAtom,
  serverLogsRangeFilterAtom,
} from 'layout/Header/HeaderState';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { toUTC } from 'utils/dateUtils';

function formatToNow(fileName: string) {
  const parts = fileName.split('_');
  if (parts.length > 1) {
    fileName = parts[parts.length - 1];
  }
  // 'sized' log file name will give 6 parts i.e., ['io.log.2023', '07', '27', '05', '57', '41']
  if (fileName.split('-').length > 4) {
    return formatDistanceToNow(
      parse(
        `${fileName.replace('io.log.', '')}Z`,
        'yyyy-MM-dd-HH-mm-ssX',
        new Date(),
      ),
      { addSuffix: true },
    );
  } else {
    // Sampe file name for hourly log: io.log.2023-07-27-06, sized log: io.log.2023-07-27-05-57-41
    // Splitting the hourly log file name by '-' will give 4 parts i.e., ['io.log.2023', '07', '27', '06']
    return formatDistanceToNow(
      parse(
        `${fileName.replace('io.log.', '')}:00:00Z`,
        'yyyy-MM-dd-HH:mm:ssX',
        new Date(),
      ),
      { addSuffix: true },
    );
  }
}

async function getLogs(platform: Platform, filter: LogFilter) {
  const [promise] = platform.sendRequest(
    {
      type: 'Log',
      data: filter.filter ? { filter } : { listFiles: filter },
    },
    { appUid: 'cloudio' },
  );
  const response = await promise;
  return response.status === 'success' ? response.data : [];
}

async function getLogEntries(
  platform: Platform,
  fileName: string,
): Promise<LogFile | null> {
  const [promise] = platform.sendRequest(
    {
      type: 'Log',
      data: { fileName },
    },
    { appUid: 'cloudio' },
  );
  const response = await promise;
  return response.status === 'success' ? response.data : null;
}

async function getPublicLogEntries(
  platform: Platform,
  fileName: string,
  key: string,
) {
  const response = await fetch(`${getUrl()}logs/${key}`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: stringify({ fileName }),
  });

  if (response.status === 403) {
    window.location.href = '#/';
    return null;
  }

  const json = (await response.json()) as LogFile;
  if (json) {
    if (json.logEntries?.length) {
      return json;
    }
  }
  platform.showError(JSON.stringify(json));
  return null;
}

async function getPublicLogs(
  platform: Platform,
  filter: LogFilter,
  key: string,
) {
  const response = await fetch(`${getUrl()}logs/${key}`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: stringify(filter.filter ? { filter } : { listFiles: filter }),
  });
  if (response.status === 403) {
    window.location.href = '#/';
    return [];
  }
  const json = await response.json();
  if (Array.isArray(json)) {
    return json;
  } else {
    platform.showError(json.message ?? JSON.stringify(json));
  }
}

export function ServerLogsFilter({
  platform,
  publicLogKey,
  standalone,
}: {
  platform: Platform;
  publicLogKey?: string;
  standalone?: boolean;
}) {
  const [filter, setFilter] = useStateXAtom(serverLogsRangeFilterAtom);
  const [logs, setLogs] = useStateXAtom(serverLogsEntriesAtom);
  const [activeLog, setActiveLog] = useStateXAtom(serverLogsActiveEntryAtom);
  const [showFilter, setShowFilter] = useState(logs.length === 0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [useUtc, setUseUtc] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const doSubmit = useEvent(async () => {
    const payload = {
      ...filter,
      fromDate: useUtc ? toUTC(filter.fromDate) : filter.fromDate,
      toDate: filter.toDate
        ? useUtc
          ? toUTC(filter.toDate)
          : filter.toDate
        : undefined,
    };

    let json;
    if (publicLogKey) {
      json = await getPublicLogs(platform, payload, publicLogKey);
    } else {
      json = await getLogs(platform, payload);
    }

    if (Array.isArray(json) && json.length > 0) {
      let logRows = json as LogFile[] | string[];
      if (typeof logRows[0] === 'string') {
        (logRows as string[]).sort((a, b) => b.localeCompare(a));
        logRows = logRows.map((l) => ({
          fileName: l,
        })) as LogFile[];
        fetchLog(logRows[0].fileName);
      } else {
        logRows = (logRows as LogFile[]).sort((a, b) =>
          b.fileName.localeCompare(a.fileName),
        );
        setActiveLog(logRows[0]);
      }
      setLogs(logRows);
      setShowFilter(false);
    } else {
      setLogs([]);
      setActiveLog(null);
    }
  });

  useEffect(() => {
    doSubmit();
  }, []);

  async function fetchLog(fileName: string) {
    let logFile: LogFile | null = null;
    if (publicLogKey) {
      logFile = await getPublicLogEntries(platform, fileName, publicLogKey);
    } else {
      logFile = await getLogEntries(platform, fileName);
    }
    if (logFile) {
      setActiveLog(logFile);
      setLogs((_logs) => {
        const index = _logs.findIndex((l) => l.fileName === fileName);
        const newLogs = [..._logs];
        newLogs[index] = logFile as LogFile;
        return newLogs;
      });
    } else {
      platform.showError(
        `Failed to load log file ${fileName}! File may have been deleted.`,
      );
    }
  }

  if (logs === serverLogsEntriesAtom.defaultValue) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: '4px 20px',
        height: 40,
        bgcolor: 'background.paper',
      }}
    >
      {showFilter ? (
        <>
          <DateTimePicker
            value={filter.fromDate}
            onChange={function (date: Date | null): void {
              if (date) {
                setFilter({ ...filter, fromDate: date });
              }
            }}
            renderInput={(p: JSX.IntrinsicAttributes & TextFieldProps) => (
              <TextField
                {...p}
                fullWidth
                variant="standard"
                inputProps={{
                  ...p.inputProps,
                  placeholder: 'From Date & Time',
                }}
              />
            )}
          />
          <DateTimePicker
            value={filter.toDate ? filter.toDate : null}
            onChange={function (date: Date | null): void {
              if (date) {
                setFilter({ ...filter, toDate: date });
              }
            }}
            renderInput={(p: JSX.IntrinsicAttributes & TextFieldProps) => (
              <TextField
                {...p}
                fullWidth
                variant="standard"
                inputProps={{
                  ...p.inputProps,
                  placeholder: 'To Date & Time',
                }}
              />
            )}
          />
          <TextField
            fullWidth
            variant="standard"
            placeholder="Filter..."
            value={filter.filter}
            onChange={(e) => {
              setFilter({ ...filter, filter: e.target.value });
            }}
            InputProps={{
              endAdornment: filter.filter ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setFilter({ ...filter, filter: '' });
                      inputRef.current?.focus();
                    }}
                  >
                    <Icon iconName="times" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            inputRef={inputRef}
          />
          <Button
            variant="text"
            onClick={() => {
              setShowFilter(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={doSubmit}>
            Submit
          </Button>
          <Tooltip
            title="When enabled, the selected dates will be interpreted in UTC time."
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={useUtc}
                  onChange={(e) => setUseUtc(e.target.checked)}
                />
              }
              label="Use UTC"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.8em',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            onClick={handleClickListItem}
            data-tip="Choose a log file"
            data-tip-delay={0}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {activeLog ? (
              <>
                {activeLog.fileName}
                <Icon iconName="calendar" />
                {formatToNow(activeLog.fileName)}
              </>
            ) : (
              'Select a Log File'
            )}
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {logs.map((log, index) => (
              <MenuItem
                key={log.fileName}
                selected={log === activeLog}
                onClick={async () => {
                  if (log.logEntries) {
                    setActiveLog(log);
                  } else {
                    fetchLog(log.fileName);
                  }
                  handleClose();
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {log.fileName}
                <Icon iconName="calendar" />
                {formatToNow(log.fileName)}
              </MenuItem>
            ))}
          </Menu>
          <IconButton
            data-tip="Filter Logs"
            color="primary"
            onClick={() => {
              setShowFilter(true);
            }}
          >
            <Icon iconName="search" color="primary" />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            data-tip="Refresh Log"
            disabled={!activeLog?.logEntries?.length}
            onClick={() => {
              if (activeLog) fetchLog(activeLog.fileName);
            }}
          >
            <Icon
              iconName="redo"
              color={activeLog?.logEntries?.length ? 'primary' : 'disabled'}
            />
          </IconButton>
          <IconButton
            data-tip="Download Log"
            disabled={!activeLog?.logEntries?.length}
            onClick={() => {
              if (activeLog?.logEntries) {
                const blob = new Blob([activeLog.logEntries.join('\n')], {
                  type: 'application/json;charset=utf-8',
                });
                FileSaver.saveAs(blob, `${activeLog.fileName}.log`);
              }
            }}
          >
            <Icon
              iconName="download"
              color={activeLog?.logEntries?.length ? 'primary' : 'disabled'}
            />
          </IconButton>
          {standalone ? null : (
            <IconButton
              data-tip="Open In New Tab"
              LinkComponent="a"
              href="/#/_/logs"
              target="_blank"
            >
              <Icon iconName="external-link" color={'primary'} />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}
